import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CreditCard, User, Loader2, CheckCircle2, ImageIcon, MapPin, Calendar, Shield, ChevronRight, Lock, Gift, X, Ticket, Truck, Wallet, Store, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { base44 } from '@/api/base44Client';
import { useCart } from '@/lib/CartContext';
import DisclaimerForm from '@/components/booking/DisclaimerForm';
import GoogleReviewsOptIn from '@/components/checkout/GoogleReviewsOptIn';
import { trackBeginCheckout, trackPurchase, trackAdsConversion } from '@/lib/analytics';
import { findShippingRate, weightToGrams, totalWeightGrams } from '@/lib/shipping';
import SplitDeliveryChoice from '@/components/checkout/SplitDeliveryChoice';

const SQUARE_APP_ID = 'sq0idp-HblpXluK_lgmGh2wdXzdCA';
const SQUARE_LOCATION_ID = 'LHHYN7QJW6P94';

const inputCls = "bg-white/5 border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60 focus:bg-white/8 transition-all duration-200 rounded-none";
const labelCls = "text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block";

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2 } },
};

const SectionHeader = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="w-7 h-7 bg-primary/10 border border-primary/20 flex items-center justify-center">
      <Icon className="w-3.5 h-3.5 text-primary" />
    </div>
    <p className="text-white/70 text-[10px] font-bold tracking-[0.15em]">{label}</p>
    <div className="flex-1 h-px bg-white/5" />
  </div>
);

const StepPill = ({ num, label, active, done }) => (
  <div className={`flex items-center gap-2 transition-all duration-300 ${active ? 'opacity-100' : done ? 'opacity-50' : 'opacity-20'}`}>
    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black transition-all duration-300 ${
      done ? 'bg-primary/30 border border-primary/40 text-primary' : active ? 'bg-primary text-white' : 'bg-white/10 text-white/40'
    }`}>
      {done ? <CheckCircle2 className="w-3 h-3" /> : num}
    </div>
    <span className={`text-[10px] font-bold tracking-widest hidden sm:block ${active ? 'text-white' : 'text-white/40'}`}>{label}</span>
  </div>
);

export default function Checkout() {
  const { items, totalPrice, clearCart, hasOnlyBookings, requiresShipping, hasProducts, chargeableShipping, fulfilmentMethod, setFulfilmentMethod, isPickup } = useCart();
  const navigate = useNavigate();

  const [step, setStep] = useState('loading');
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    address_line1: '', address_line2: '', city: '', postcode: ''
  });
  const [billingForm, setBillingForm] = useState({
    address_line1: '', address_line2: '', city: '', postcode: ''
  });
  const [billingIsSameAsDelivery, setBillingIsSameAsDelivery] = useState(true);
  const [saveAddress, setSaveAddress] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingDisclaimer, setPendingDisclaimer] = useState(null);
  const [disclaimerDone, setDisclaimerDone] = useState(false);
  const [card, setCard] = useState(null);
  const [cardToken, setCardToken] = useState(null); // stored for 3DS retry
  const [afterpayUnavailable, setAfterpayUnavailable] = useState(null);
  const afterpayRef = useRef(null);
  const afterpayPayRef = useRef(null);

  // Apple Pay (Square Web Payments SDK) — native button on verified domains (Safari/iOS/macOS)
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const applePayRef = useRef(null);
  const applePayPayRef = useRef(null);

  // Gift card state
  const [codeTab, setCodeTab] = useState('gift');
  const [giftCardCode, setGiftCardCode] = useState('');
  const [purchasedGiftCards, setPurchasedGiftCards] = useState([]);
  const [joinMailingList, setJoinMailingList] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);
  const [checkedSubscription, setCheckedSubscription] = useState(false);
  const [createAccount, setCreateAccount] = useState(false);
  const [accountPassword, setAccountPassword] = useState('');
  const [accountCreated, setAccountCreated] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null); // { orderId, email, productIds, hasShippable }
  const [appliedGiftCard, setAppliedGiftCard] = useState(null); // { code, balance, appliedAmount }
  const [checkingCode, setCheckingCode] = useState(false);
  const [codeError, setCodeError] = useState('');

  // Discount code state
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null); // { code, discountAmount, description, type, value }
  const [checkingDiscount, setCheckingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState('');

  // Shipping state
  const [shippingRate, setShippingRate] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingRates, setShippingRates] = useState([]);
  const [productMeta, setProductMeta] = useState({});

  // Delivery option for a basket containing an out-of-stock line
  const [deliveryOption, setDeliveryOption] = useState('together');

  const eligibleSubtotal = items.filter(i => i.type !== 'giftcard').reduce((s, i) => s + i.price * (i.quantity || 1), 0);
  const discountAmount = appliedDiscount ? appliedDiscount.discountAmount : 0;
  const discountedTotal = Math.max(0, totalPrice - discountAmount);

  // Product lines that are out of stock / backordered — these drive the
  // "ship together" vs "ship available items now" choice.
  const unavailableItems = items.filter(i => {
    if (i.type !== 'product') return false;
    const meta = productMeta[i.product_id];
    if (!meta) return false;
    if (meta.in_stock === false) return true;
    if (meta.stock_quantity != null && Number(meta.stock_quantity) < (i.quantity || 1)) return true;
    return false;
  });
  const hasBackorder = unavailableItems.length > 0 && !isPickup;

  // Postage for the delayed items' own parcel (a second postage charge).
  const secondPostage = hasBackorder
    ? Number(findShippingRate(shippingRates, totalWeightGrams(unavailableItems, productMeta))?.rate) || 0
    : 0;

  const shippingAmount = (chargeableShipping && shippingRate) ? Number(shippingRate.rate) || 0 : 0;
  const delayedShippingAmount = (hasBackorder && deliveryOption === 'split') ? secondPostage : 0;
  const giftCardAppliedAmount = appliedGiftCard
    ? Math.min(appliedGiftCard.balance, discountedTotal + shippingAmount + delayedShippingAmount)
    : 0;
  const chargeAmount = Math.max(0, discountedTotal + shippingAmount + delayedShippingAmount - giftCardAppliedAmount);
  const fullyCoveredByGiftCard = (appliedGiftCard || appliedDiscount) && chargeAmount <= 0;
  const hasGiftCardItem = items.some(i => i.type === 'giftcard');

  useEffect(() => {
    if (items.length === 0 && step !== 'success') navigate('/cart');
  }, [items]);

  const checkSubscription = async (email) => {
    if (!email) return;
    try {
      const res = await base44.functions.invoke('checkNewsletterSubscription', { email });
      const subscribed = !!res.data?.subscribed;
      setAlreadySubscribed(subscribed);
      setJoinMailingList(subscribed);
      setCheckedSubscription(true);
    } catch {
      setCheckedSubscription(true);
    }
  };

  useEffect(() => {
    base44.auth.isAuthenticated().then(async (authed) => {
      if (authed) {
        const user = await base44.auth.me();
        setLoggedInUser(user);
        setForm(f => ({
          ...f,
          name: [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.full_name || '',
          email: user.email || '',
          phone: user.phone || '',
          address_line1: user.delivery_address_line1 || '',
          address_line2: user.delivery_address_line2 || '',
          city: user.delivery_city || '',
          postcode: user.delivery_postcode || '',
        }));
        if (user.email) checkSubscription(user.email);
      }
      setStep('details');
    });
  }, []);

  useEffect(() => {
    if (items.length > 0) trackBeginCheckout(items, totalPrice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch active shipping rates + product weights to compute shipping fee
  useEffect(() => {
    if (!chargeableShipping) { setShippingRate(null); return; }
    let cancelled = false;
    (async () => {
      setShippingLoading(true);
      try {
        const rates = await base44.entities.ShippingRate.list('sort_order', 100);
        const productIds = [...new Set(items.filter(i => i.type === 'product').map(i => i.product_id).filter(Boolean))];
        const meta = {};
        await Promise.all(productIds.map(async (id) => {
          try {
            const p = await base44.entities.Product.get(id);
            if (p) meta[id] = { weight: p.weight, weight_unit: p.weight_unit, in_stock: p.in_stock, stock_quantity: p.stock_quantity };
          } catch {}
        }));
        let totalG = 0;
        for (const i of items.filter(it => it.type === 'product')) {
          const w = meta[i.product_id];
          totalG += weightToGrams(w?.weight, w?.weight_unit) * (i.quantity || 1);
        }
        if (!cancelled) {
          setShippingRates(rates || []);
          setProductMeta(meta);
          setShippingRate(findShippingRate(rates, totalG));
        }
      } catch {}
      if (!cancelled) setShippingLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, chargeableShipping]);

  useEffect(() => {
    if (step !== 'payment') return;
    if (fullyCoveredByGiftCard) return; // no card form needed
    const existing = document.getElementById('sq-sdk');
    if (existing) { initSquare(); return; }
    const script = document.createElement('script');
    script.id = 'sq-sdk';
    script.src = 'https://web.squarecdn.com/v1/square.js';
    script.onload = () => initSquare();
    document.head.appendChild(script);
  }, [step, fullyCoveredByGiftCard]);

  // Initialise / re-initialise the Clearpay (Afterpay) button whenever the chargeable amount changes.
  // Clearpay tokens are bound to the PaymentRequest total, so we must re-create the button if the
  // amount changes (e.g. after a gift card or discount is applied).
  useEffect(() => {
    if (step !== 'payment') return;
    const clearAp = () => { if (afterpayRef.current) { try { afterpayRef.current.destroy(); } catch {} afterpayRef.current = null; } };
    if (fullyCoveredByGiftCard) { clearAp(); setAfterpayUnavailable(null); return; }
    // Afterpay/Clearpay does not support gift card purchases
    if (hasGiftCardItem) { clearAp(); setAfterpayUnavailable('Clearpay is not available when purchasing gift cards — pay by card instead.'); return; }

    let cancelled = false;
    const init = async () => {
      try {
        if (!window.Square) {
          await new Promise((resolve, reject) => {
            if (window.Square) return resolve();
            const existing = document.getElementById('sq-sdk');
            if (existing) { existing.addEventListener('load', () => resolve()); existing.addEventListener('error', () => reject(new Error('SDK load error'))); return; }
            const s = document.createElement('script');
            s.id = 'sq-sdk';
            s.src = 'https://web.squarecdn.com/v1/square.js';
            s.onload = () => resolve();
            s.onerror = () => reject(new Error('SDK load error'));
            document.head.appendChild(s);
          });
        }
        if (cancelled) return;
        const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
        if (afterpayRef.current) { try { await afterpayRef.current.destroy(); } catch {} afterpayRef.current = null; }
        const paymentRequest = payments.paymentRequest({
          countryCode: 'GB',
          currencyCode: 'GBP',
          total: { amount: chargeAmount.toFixed(2), label: 'Total' },
          requestShippingContact: false,
        });
        const ap = await payments.afterpayClearpay(paymentRequest);
        if (cancelled) { try { await ap.destroy(); } catch {} return; }
        const container = document.getElementById('sq-afterpay');
        if (!container) { try { await ap.destroy(); } catch {} return; }
        container.innerHTML = '';
        await ap.attach('#sq-afterpay');
        container.onclick = () => { if (afterpayPayRef.current) afterpayPayRef.current(); };
        afterpayRef.current = ap;
        setAfterpayUnavailable(null);
      } catch (e) {
        if (cancelled) return;
        afterpayRef.current = null;
        // AfterpayAmountOutsideLimitsError and others (e.g. not enabled on account) land here
        setAfterpayUnavailable(e?.message || 'Clearpay unavailable for this order');
      }
    };
    init();
    return () => { cancelled = true; clearAp(); };
  }, [step, chargeAmount, fullyCoveredByGiftCard, hasGiftCardItem]);

  // Initialise the Apple Pay button whenever the chargeable amount changes.
  // Apple Pay only renders on verified domains + Safari; hide the section silently elsewhere.
  useEffect(() => {
    if (step !== 'payment') return;
    const clearAp = () => { if (applePayRef.current) { try { applePayRef.current.destroy(); } catch {} applePayRef.current = null; } };
    if (fullyCoveredByGiftCard || hasGiftCardItem) { clearAp(); setApplePayAvailable(false); return; }

    let cancelled = false;
    const init = async () => {
      try {
        if (!window.Square) {
          await new Promise((resolve, reject) => {
            if (window.Square) return resolve();
            const existing = document.getElementById('sq-sdk');
            if (existing) { existing.addEventListener('load', () => resolve()); existing.addEventListener('error', () => reject(new Error('SDK load error'))); return; }
            const s = document.createElement('script');
            s.id = 'sq-sdk';
            s.src = 'https://web.squarecdn.com/v1/square.js';
            s.onload = () => resolve();
            s.onerror = () => reject(new Error('SDK load error'));
            document.head.appendChild(s);
          });
        }
        if (cancelled) return;
        const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
        if (applePayRef.current) { try { await applePayRef.current.destroy(); } catch {} applePayRef.current = null; }
        const paymentRequest = payments.paymentRequest({
          countryCode: 'GB',
          currencyCode: 'GBP',
          total: { amount: chargeAmount.toFixed(2), label: 'Motion Reality' },
        });
        const ap = await payments.applePay(paymentRequest);
        if (cancelled) { try { await ap.destroy(); } catch {} return; }
        const container = document.getElementById('sq-applepay');
        if (!container) { try { await ap.destroy(); } catch {} return; }
        container.innerHTML = '';
        await ap.attach('#sq-applepay');
        container.onclick = () => { if (applePayPayRef.current) applePayPayRef.current(); };
        applePayRef.current = ap;
        setApplePayAvailable(true);
      } catch (e) {
        if (cancelled) return;
        applePayRef.current = null;
        setApplePayAvailable(false);
      }
    };
    init();
    return () => { cancelled = true; clearAp(); };
  }, [step, chargeAmount, fullyCoveredByGiftCard, hasGiftCardItem]);

  const initSquare = async () => {
    if (!window.Square) return;
    try {
      if (card) { try { await card.destroy(); } catch {} }
      const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
      const cardInstance = await payments.card({
        style: {
          '.input-container': { borderColor: 'rgba(255,255,255,0.08)', borderRadius: '0px' },
          input: { color: '#fff', backgroundColor: 'rgba(255,255,255,0.04)', fontSize: '14px' },
          'input::placeholder': { color: 'rgba(255,255,255,0.2)' },
          '.message-text': { color: 'rgba(255,255,255,0.4)' },
        }
      });
      await cardInstance.attach('#sq-card');
      setCard(cardInstance);
    } catch (err) {
      console.error('Square card init failed:', err);
      setError('Could not load the card form. Please refresh the page or try again.');
    }
  };

  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { setError('Name and email are required.'); return; }
    if (!form.phone || !form.phone.trim()) { setError('Phone number is required to continue.'); return; }
    if (requiresShipping) {
      const addressLabel = isPickup ? 'billing address' : 'delivery address';
      if (!form.address_line1 || !form.city || !form.postcode) { setError(`Please complete your ${addressLabel}.`); return; }
      if (!isPickup && !billingIsSameAsDelivery && (!billingForm.address_line1 || !billingForm.city || !billingForm.postcode)) {
        setError('Please complete your billing address.'); return;
      }
    }
    setError('');
    if (loggedInUser) {
      const updates = {};
      // Persist the entered phone number to the account if it isn't already saved there
      if (form.phone && !loggedInUser.phone) {
        updates.phone = form.phone;
      }
      if (saveAddress && requiresShipping) {
        updates.delivery_address_line1 = form.address_line1;
        updates.delivery_address_line2 = form.address_line2;
        updates.delivery_city = form.city;
        updates.delivery_postcode = form.postcode;
      }
      if (Object.keys(updates).length > 0) {
        try { await base44.auth.updateMe(updates); } catch {}
      }
    }
    setStep('payment');
  };

  const handleApplyGiftCard = async () => {
    const code = giftCardCode.trim().toUpperCase();
    if (!code) { setCodeError('Enter a gift card code.'); return; }
    setCheckingCode(true);
    setCodeError('');
    try {
      const res = await base44.functions.invoke('getGiftCardBalance', { code });
      if (res.data?.valid) {
        setAppliedGiftCard({
          code,
          balance: res.data.balance,
        });
      } else {
        setCodeError(res.data?.error || 'Invalid gift card.');
      }
    } catch (err) {
      setCodeError('Could not check gift card. Please try again.');
    }
    setCheckingCode(false);
  };

  const handleRemoveGiftCard = () => {
    setAppliedGiftCard(null);
    setGiftCardCode('');
    setCodeError('');
  };

  const handleApplyDiscount = async () => {
    const code = discountCodeInput.trim().toUpperCase();
    if (!code) { setDiscountError('Enter a discount code.'); return; }
    setCheckingDiscount(true);
    setDiscountError('');
    try {
      const res = await base44.functions.invoke('validateDiscountCode', {
        code, subtotal: eligibleSubtotal, customerEmail: form.email,
        items: items.map(i => i.type === 'giftcard' ? ({
          type: 'giftcard', price: i.price, quantity: i.quantity,
        }) : i.type === 'booking' ? ({
          type: 'booking', price: i.price, quantity: i.quantity,
        }) : ({
          type: 'product', productId: i.product_id, price: i.price, quantity: i.quantity,
        })),
      });
      if (res.data?.valid) {
        setAppliedDiscount({
          code: res.data.code,
          discountAmount: res.data.discountAmount,
          description: res.data.description,
          type: res.data.type,
          value: res.data.value,
          excludedItems: res.data.excludedItems || [],
        });
      } else {
        setDiscountError(res.data?.error || 'Invalid discount code.');
      }
    } catch (err) {
      setDiscountError('Could not validate discount code. Please try again.');
    }
    setCheckingDiscount(false);
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCodeInput('');
    setDiscountError('');
  };

  // Shared commit step — both card and Clearpay flow through this.
  // For card: sourceId = card token, verificationToken = 3DS token.
  // For Clearpay: sourceId = afterpay token, verificationToken = null.
  const commitCheckout = async (sourceId, verificationToken, methodOverride) => {
    let res;
    try {
      res = await base44.functions.invoke('squareCartCheckout', {
        sourceId: sourceId || undefined,
        verificationToken: verificationToken || undefined,
        paymentMethod: methodOverride || (verificationToken ? 'card' : 'afterpay'),
        amount: totalPrice,
        discount: appliedDiscount ? { code: appliedDiscount.code } : undefined,
        giftCard: appliedGiftCard ? { code: appliedGiftCard.code } : undefined,
        customerName: form.name,
        customerEmail: form.email,
        customerPhone: form.phone,
        fulfilmentMethod,
        deliveryOption,
        deliveryAddressLine1: form.address_line1,
        deliveryAddressLine2: form.address_line2,
        deliveryCity: form.city,
        deliveryPostcode: form.postcode,
        items: items.map(i => i.type === 'booking' ? ({
          type: 'booking',
          experienceType: i.experience_type,
          rigType: i.rig_type,
          date: i.date,
          timeSlot: i.time_slot,
          duration: i.duration,
          price: i.price,
          quantity: 1,
          customerName: i.customer_name || form.name,
          customerEmail: i.customer_email || form.email,
          customerPhone: i.customer_phone || form.phone,
          notes: i.notes || '',
          referralSource: i.referral_source || '',
        }) : i.type === 'giftcard' ? ({
          type: 'giftcard',
          price: i.price,
          quantity: 1,
          giftCardMode: i.gift_card_mode,
          experienceType: i.experience_type,
          deliveryMethod: i.delivery_method,
          sendToSelf: i.send_to_self,
          recipientName: i.recipient_name,
          recipientEmail: i.recipient_email,
          recipientAddressLine1: i.recipient_address_line1,
          recipientAddressLine2: i.recipient_address_line2,
          recipientCity: i.recipient_city,
          recipientPostcode: i.recipient_postcode,
          giftMessage: i.gift_message,
          productName: i.product_name,
        }) : ({
          type: 'product',
          productId: i.product_id,
          productName: i.product_name,
          price: i.price,
          quantity: i.quantity,
          delayed: unavailableItems.includes(i),
        })),
      });
    } catch (err) {
      setCardToken(null);
      setLoading(false);
      if (err?.response?.status === 400) {
        const body = err.response.data;
        setError(body?.error || 'Payment was declined. Please check your details and try again.');
      } else {
        setError('A system error occurred. Please try again or contact support.');
      }
      return false;
    }

    setLoading(false);
    if (res.data?.success) {
      setCardToken(null);
      setPurchasedGiftCards(res.data.giftCards || []);
      // Capture booking context for the post-checkout disclaimer (only if cart contained bookings)
      const bookings = (res.data.orders || []).filter(o => o.experience_type);
      if (items.some(i => i.type === 'booking') && bookings.length > 0) {
        setPendingDisclaimer({
          bookingIds: bookings.map(b => b.id),
          experienceType: bookings[0].experience_type,
          sessionDate: bookings[0].date,
          paymentId: res.data.paymentId || '',
        });
      }
      trackPurchase(res.data.orderId || res.data.paymentId || 'unknown', items, totalPrice);
      trackAdsConversion(res.data.orderId || res.data.paymentId, totalPrice);
      setCompletedOrder({
        orderId: res.data.orderId || res.data.paymentId || '',
        email: form.email,
        productIds: items.filter(i => i.type === 'product').map(i => i.product_id).filter(Boolean),
        hasShippable: items.some(i => i.type === 'product'),
      });
      if (joinMailingList && !alreadySubscribed && form.email) {
        base44.functions.invoke('subscribeToNewsletter', { email: form.email, source: 'checkout' }).catch(() => {});
      }
      if (createAccount && !loggedInUser && form.email && accountPassword && accountPassword.length >= 6) {
        base44.auth.register({ email: form.email, password: accountPassword, full_name: form.name })
          .then(() => setAccountCreated(true))
          .catch((e) => { console.error('Account creation failed:', e?.message); });
      }
      await clearCart();
      if (res.data?.redeemError) {
        setError(`Payment confirmed (ID: ${res.data.paymentId}), but your gift card could not be applied. Please contact support quoting this payment ID and your gift card code.`);
      } else if (res.data?.partialFailure) {
        setError(`Payment confirmed (ID: ${res.data.paymentId}), but we had trouble saving your order. Please contact support quoting this payment ID.`);
      }
      setStep('success');
      return true;
    } else {
      setCardToken(null);
      setError(res.data?.error || 'Payment failed. Please try again.');
      return false;
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    let cardSourceToken = null;
    let verifiedToken = null;

    if (chargeAmount > 0) {
      if (!card) { setError('Card form not ready. Please wait.'); setLoading(false); return; }

      cardSourceToken = cardToken;
      if (!cardSourceToken) {
        const result = await card.tokenize();
        if (result.status !== 'OK') {
          setError(result.errors?.[0]?.message || 'Card error. Please check your details.');
          setLoading(false);
          return;
        }
        cardSourceToken = result.token;
        setCardToken(cardSourceToken);
      }

      const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);

      try {
        const verification = await payments.verifyBuyer(cardSourceToken, buildVerificationDetails());
        verifiedToken = verification.token;
      } catch (err) {
        if (err.name === 'ThreeDSMethodTimeoutError') {
          setError('Your bank took too long to respond. Please try again to complete verification.');
          setLoading(false);
          return;
        }
        throw err;
      }

      if (!verifiedToken) {
        setError('3D Secure verification failed. Please try again.');
        setLoading(false);
        return;
      }
    }

    await commitCheckout(cardSourceToken || undefined, verifiedToken);
  };

  // Clearpay path: tokenize the Afterpay/Clearpay payment (opens Clearpay's popup),
  // then commit using the returned token — no 3DS verification needed.
  const handleAfterpayPay = async () => {
    const ap = afterpayRef.current;
    if (!ap) return;
    setLoading(true);
    setError('');
    try {
      const tokenResult = await ap.tokenize();
      if (tokenResult.status !== 'OK') {
        setError(tokenResult.errors?.[0]?.message || 'Clearpay payment was not completed.');
        setLoading(false);
        return;
      }
      await commitCheckout(tokenResult.token, null);
    } catch (err) {
      setError(err?.message || 'Clearpay payment failed. Please try again.');
      setLoading(false);
    }
  };

  // Shared SCA verification details (used by the card + Apple Pay flows)
  const buildVerificationDetails = () => ({
    amount: chargeAmount.toFixed(2),
    currencyCode: 'GBP',
    intent: 'CHARGE',
    billingContact: {
      familyName: form.name.split(' ').slice(1).join(' ') || form.name,
      givenName: form.name.split(' ')[0],
      email: form.email,
      phone: form.phone || undefined,
      addressLines: [form.address_line1, form.address_line2].filter(Boolean),
      city: form.city,
      postalCode: form.postcode,
      countryCode: 'GB',
    },
  });

  // Apple Pay path: open the native Apple Pay sheet, verify the buyer for SCA,
  // then commit using the same squareCartCheckout flow as a card payment.
  const handleApplePayPay = async () => {
    const ap = applePayRef.current;
    if (!ap) return;
    setLoading(true);
    setError('');
    try {
      const tokenResult = await ap.tokenize();
      if (tokenResult.status !== 'OK') {
        setError(tokenResult.errors?.[0]?.message || 'Apple Pay payment was not completed.');
        setLoading(false);
        return;
      }
      const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
      let verifiedToken = null;
      try {
        const verification = await payments.verifyBuyer(tokenResult.token, buildVerificationDetails());
        verifiedToken = verification.token;
      } catch (err) {
        if (err?.name === 'ThreeDSMethodTimeoutError') {
          setError('Your bank took too long to respond. Please try again to complete verification.');
          setLoading(false);
          return;
        }
        throw err;
      }
      await commitCheckout(tokenResult.token, verifiedToken, 'applepay');
    } catch (err) {
      setCardToken(null);
      setError(err?.message || 'Apple Pay payment failed. Please try again.');
      setLoading(false);
    }
  };

  // Keep the click handler refs pointed at the latest closures (avoids stale state)
  applePayPayRef.current = handleApplePayPay;
  afterpayPayRef.current = handleAfterpayPay;

  return (
    <div className="min-h-screen bg-[#0a0a0c] pt-16">

      {/* Top bar */}
      <div className="border-b border-white/5 bg-[#0d0d0f]/80 backdrop-blur-sm sticky top-[var(--header-offset)] z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          {step !== 'success' ? (
            <Link to="/cart" className="inline-flex items-center gap-1.5 text-white/30 hover:text-white/70 text-xs font-medium transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Cart
            </Link>
          ) : <div />}

          {step !== 'loading' && step !== 'success' && (
            <div className="flex items-center gap-4">
              <StepPill num="1" label="DETAILS" active={step === 'details'} done={step === 'payment'} />
              <ChevronRight className="w-3 h-3 text-white/10" />
              <StepPill num="2" label="PAYMENT" active={step === 'payment'} done={false} />
            </div>
          )}

          <div className="flex items-center gap-1.5 text-white/20">
            <Lock className="w-3 h-3" />
            <span className="text-[10px] font-bold tracking-wider hidden sm:block">SECURE CHECKOUT</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">

        {/* Loading */}
        {step === 'loading' && (
          <div className="flex justify-center items-center py-32">
            <div className="flex flex-col items-center gap-4">
              <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
              <p className="text-white/30 text-xs tracking-widest">LOADING...</p>
            </div>
          </div>
        )}

        {/* Success */}
        <AnimatePresence>
          {step === 'success' && pendingDisclaimer && !disclaimerDone && (
            <motion.div
              key="disclaimer"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="py-10"
            >
              <div className="max-w-3xl mx-auto mb-8 text-center">
                <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 px-3 py-1.5 mb-4">
                  <Shield className="w-3.5 h-3.5 text-primary" />
                  <span className="text-primary text-[10px] font-bold tracking-widest">ACTION REQUIRED</span>
                </div>
                <h1 className="text-white font-black text-3xl uppercase mb-2">Complete Your Disclaimer</h1>
                <p className="text-white/40 text-sm">Your booking is confirmed. Before your session, please read and sign the simulator disclaimer below.</p>
              </div>
              <DisclaimerForm
                prefillName={form.name}
                prefillEmail={form.email}
                bookingContext={pendingDisclaimer}
                onComplete={() => setDisclaimerDone(true)}
              />
              <p className="max-w-3xl mx-auto mt-6 text-white/30 text-xs">
                This signed disclaimer and your emergency contact details are required before your session. Please complete it now to save time on the day.
              </p>
            </motion.div>
          )}
          {step === 'success' && (!pendingDisclaimer || disclaimerDone) && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="max-w-lg mx-auto text-center py-24"
            >
              <motion.div
                initial={{ scale: 0, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.15, type: 'spring', stiffness: 180, damping: 14 }}
                className="relative w-24 h-24 mx-auto mb-8"
              >
                <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl" />
                <div className="relative w-24 h-24 bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
                  <CheckCircle2 className="w-12 h-12 text-white" />
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <p className="text-primary text-[10px] font-bold tracking-[0.2em] mb-3">PAYMENT SUCCESSFUL</p>
                <h1 className="text-white font-black text-4xl uppercase mb-4 leading-tight">Order<br />Confirmed!</h1>
                <p className="text-white/40 text-sm mb-6 leading-relaxed">
                  A confirmation has been sent to{' '}
                  <span className="text-white font-medium">{form.email}</span>
                </p>
                {completedOrder?.hasShippable && (
                  <GoogleReviewsOptIn
                    orderId={completedOrder.orderId}
                    email={completedOrder.email}
                    productIds={completedOrder.productIds}
                  />
                )}
                {accountCreated && (
                  <div className="bg-primary/5 border border-primary/20 p-4 mb-10 text-left">
                    <p className="text-primary text-[10px] font-bold tracking-widest mb-1">ACCOUNT CREATED</p>
                    <p className="text-white/60 text-xs leading-relaxed">
                      We've created an account for <span className="text-white font-medium">{form.email}</span>. Check your inbox for a verification link to activate it and start tracking your orders.
                    </p>
                  </div>
                )}
                {!accountCreated && <div className="mb-10" />}
                {purchasedGiftCards.length > 0 && (
                  <div className="bg-white/[0.03] border border-white/10 p-6 mb-8 text-left">
                    <p className="text-primary text-[10px] font-bold tracking-widest mb-3">YOUR GIFT CARD CODES</p>
                    {purchasedGiftCards.map(gc => (
                      <div key={gc.id} className="mb-3 last:mb-0">
                        <p className="text-primary font-mono font-black text-xl tracking-[0.15em] select-all">{gc.code}</p>
                        <p className="text-white/50 text-xs mt-1">Value: £{Number(gc.amount).toFixed(2)}</p>
                      </div>
                    ))}
                    <p className="text-white/30 text-[10px] mt-3 leading-relaxed">
                      {purchasedGiftCards.length > 1 ? 'These codes have' : 'This code has'} also been emailed to you. Forward to the recipient if it's a gift.
                    </p>
                  </div>
                )}
                <Link to="/shop">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-10 py-4 transition-colors"
                  >
                    CONTINUE SHOPPING
                  </motion.button>
                </Link>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main checkout layout */}
        {(step === 'details' || step === 'payment') && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

            {/* Left: Forms */}
            <div className="lg:col-span-3">
              <AnimatePresence>

                {/* Details step */}
                {step === 'details' && (
                  <motion.form
                    key="details"
                    {...fadeUp}
                    onSubmit={handleDetailsSubmit}
                    className="space-y-8"
                  >
                    {/* Contact */}
                    <div className="bg-white/[0.02] border border-white/5 p-6">
                      <SectionHeader icon={User} label="CONTACT DETAILS" />
                      {loggedInUser && (
                        <div className="flex items-center gap-2 mb-5 bg-primary/5 border border-primary/10 px-3 py-2">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                          <span className="text-primary text-[10px] font-bold tracking-wider">SIGNED IN AS {([loggedInUser.first_name, loggedInUser.last_name].filter(Boolean).join(' ').trim() || loggedInUser.full_name || '').toUpperCase()}</span>
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label className={labelCls}>FULL NAME *</Label>
                          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                            className={inputCls} placeholder="Your full name" />
                        </div>
                        <div>
                          <Label className={labelCls}>EMAIL *</Label>
                          <Input type="email" value={form.email}
                            onChange={e => { setForm({ ...form, email: e.target.value }); setCheckedSubscription(false); }}
                            onBlur={e => { if (e.target.value && !checkedSubscription) checkSubscription(e.target.value); }}
                            className={inputCls} placeholder="your@email.com" />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className={labelCls}>PHONE *</Label>
                          <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                            className={inputCls} placeholder="Your phone number" />
                        </div>
                      </div>
                      <label className="flex items-center gap-2.5 mt-5 cursor-pointer group">
                        <input type="checkbox" checked={joinMailingList} onChange={e => setJoinMailingList(e.target.checked)}
                          className="w-4 h-4 accent-primary" />
                        <span className="text-white/40 text-xs group-hover:text-white/60 transition-colors">
                          {alreadySubscribed
                            ? "You're on our mailing list — keep this ticked to stay subscribed."
                            : 'Join our mailing list for offers, new products and event news.'}
                        </span>
                      </label>
                      {!loggedInUser && (
                        <div className="mt-5 border-t border-white/5 pt-5">
                          <label className="flex items-center gap-2.5 cursor-pointer group">
                            <input type="checkbox" checked={createAccount} onChange={e => { setCreateAccount(e.target.checked); if (!e.target.checked) setAccountPassword(''); }}
                              className="w-4 h-4 accent-primary" />
                            <span className="text-white/40 text-xs group-hover:text-white/60 transition-colors">
                              Create an account to track your orders and save your details.
                            </span>
                          </label>
                          {createAccount && (
                            <div className="mt-3">
                              <Label className={labelCls}>CREATE A PASSWORD (MIN. 6 CHARACTERS)</Label>
                              <Input type="password" value={accountPassword} onChange={e => setAccountPassword(e.target.value)}
                                className={inputCls} placeholder="Choose a password" autoComplete="new-password" />
                              <p className="text-white/20 text-[10px] mt-1.5">We'll create your account after payment and email you a verification link to activate it.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Fulfilment method — Delivery vs Collect in store */}
                    {hasProducts && (
                      <div className="bg-white/[0.02] border border-white/5 p-6">
                        <SectionHeader icon={isPickup ? Store : Truck} label="FULFILMENT" />
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setFulfilmentMethod('delivery')}
                            className={`flex items-start gap-3 p-4 border text-left transition-all ${!isPickup ? 'border-primary/50 bg-primary/5' : 'border-white/10 hover:border-white/25'}`}
                          >
                            <Truck className={`w-4 h-4 mt-0.5 shrink-0 ${!isPickup ? 'text-primary' : 'text-white/40'}`} />
                            <div>
                              <p className={`text-xs font-bold tracking-widest ${!isPickup ? 'text-white' : 'text-white/60'}`}>DELIVERY</p>
                              <p className="text-white/40 text-[10px] mt-1 leading-relaxed">Posted to your UK address · next day if before 1pm Mon–Thu</p>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setFulfilmentMethod('pickup')}
                            className={`flex items-start gap-3 p-4 border text-left transition-all ${isPickup ? 'border-primary/50 bg-primary/5' : 'border-white/10 hover:border-white/25'}`}
                          >
                            <Store className={`w-4 h-4 mt-0.5 shrink-0 ${isPickup ? 'text-primary' : 'text-white/40'}`} />
                            <div>
                              <p className={`text-xs font-bold tracking-widest ${isPickup ? 'text-white' : 'text-white/60'}`}>COLLECT IN STORE</p>
                              <p className="text-white/40 text-[10px] mt-1 leading-relaxed">Pick up from Bromborough · no shipping fee</p>
                            </div>
                          </button>
                        </div>
                        {isPickup && (
                          <div className="flex items-start gap-2 mt-4 bg-primary/5 border border-primary/15 px-3 py-2.5">
                            <Clock className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                            <p className="text-white/60 text-[11px] leading-relaxed">
                              <span className="text-white font-bold">Collection from Motion Reality, Unit 9 Croft Business Park, Bromborough CH62 3RA.</span><br />
                              Ready same day if ordered before 1pm Mon–Thu, otherwise next working day. We'll email you when it's ready to collect.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Split delivery — only when something in the basket is out of stock */}
                    {hasBackorder && (
                      <SplitDeliveryChoice
                        unavailableNames={unavailableItems.map(i => i.product_name)}
                        firstPostage={shippingAmount}
                        secondPostage={secondPostage}
                        value={deliveryOption}
                        onChange={setDeliveryOption}
                      />
                    )}

                    {/* Address (delivery address, or billing address for collection) */}
                    {requiresShipping && (
                      <div className="bg-white/[0.02] border border-white/5 p-6">
                        <SectionHeader icon={MapPin} label={isPickup ? 'BILLING ADDRESS' : 'DELIVERY ADDRESS'} />
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <Label className={labelCls}>ADDRESS LINE 1 *</Label>
                            <Input value={form.address_line1} onChange={e => setForm({ ...form, address_line1: e.target.value })}
                              className={inputCls} placeholder="Street address" />
                          </div>
                          <div>
                            <Label className={labelCls}>ADDRESS LINE 2</Label>
                            <Input value={form.address_line2} onChange={e => setForm({ ...form, address_line2: e.target.value })}
                              className={inputCls} placeholder="Apartment, suite, etc. (optional)" />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className={labelCls}>CITY *</Label>
                              <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                                className={inputCls} placeholder="City" />
                            </div>
                            <div>
                              <Label className={labelCls}>POSTCODE *</Label>
                              <Input value={form.postcode} onChange={e => setForm({ ...form, postcode: e.target.value })}
                                className={inputCls} placeholder="Postcode" />
                            </div>
                          </div>
                        </div>
                        {loggedInUser && (
                          <label className="flex items-center gap-2.5 mt-5 cursor-pointer group">
                            <input type="checkbox" checked={saveAddress} onChange={e => setSaveAddress(e.target.checked)}
                              className="w-4 h-4 accent-primary" />
                            <span className="text-white/30 text-xs group-hover:text-white/50 transition-colors">Save address to my account for future orders</span>
                          </label>
                        )}
                      </div>
                    )}

                    {/* Billing Address (delivery only — for collection, the address above is the billing address) */}
                    {requiresShipping && !isPickup && (
                      <div className="bg-white/[0.02] border border-white/5 p-6">
                        <SectionHeader icon={CreditCard} label="BILLING ADDRESS" />
                        <label className="flex items-center gap-2.5 mb-4 cursor-pointer group">
                          <input type="checkbox" checked={billingIsSameAsDelivery}
                            onChange={e => setBillingIsSameAsDelivery(e.target.checked)}
                            className="w-4 h-4 accent-primary" />
                          <span className="text-white/40 text-xs group-hover:text-white/60 transition-colors">Same as delivery address</span>
                        </label>
                        <AnimatePresence>
                          {!billingIsSameAsDelivery && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }} className="grid grid-cols-1 gap-4 overflow-hidden">
                              <div>
                                <Label className={labelCls}>ADDRESS LINE 1 *</Label>
                                <Input value={billingForm.address_line1} onChange={e => setBillingForm({ ...billingForm, address_line1: e.target.value })}
                                  className={inputCls} placeholder="Street address" />
                              </div>
                              <div>
                                <Label className={labelCls}>ADDRESS LINE 2</Label>
                                <Input value={billingForm.address_line2} onChange={e => setBillingForm({ ...billingForm, address_line2: e.target.value })}
                                  className={inputCls} placeholder="Apartment, suite, etc. (optional)" />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label className={labelCls}>CITY *</Label>
                                  <Input value={billingForm.city} onChange={e => setBillingForm({ ...billingForm, city: e.target.value })}
                                    className={inputCls} placeholder="City" />
                                </div>
                                <div>
                                  <Label className={labelCls}>POSTCODE *</Label>
                                  <Input value={billingForm.postcode} onChange={e => setBillingForm({ ...billingForm, postcode: e.target.value })}
                                    className={inputCls} placeholder="Postcode" />
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}

                    {/* Error */}
                    <AnimatePresence>
                      {error && (
                        <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                          className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-4 py-3">
                          {error}
                        </motion.p>
                      )}
                    </AnimatePresence>

                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className="w-full bg-primary hover:bg-primary/90 text-white font-black text-xs tracking-widest py-4 transition-all flex items-center justify-center gap-2.5"
                    >
                      CONTINUE TO PAYMENT <ChevronRight className="w-4 h-4" />
                    </motion.button>
                  </motion.form>
                )}

                {/* Payment step */}
                {step === 'payment' && (
                  <motion.div key="payment" {...fadeUp} className="space-y-6">

                    {/* Details summary */}
                    <div className="bg-white/[0.02] border border-white/5 p-6">
                      <div className="flex items-center justify-between mb-4">
                        <SectionHeader icon={User} label="YOUR DETAILS" />
                        <button onClick={() => setStep('details')}
                          className="text-white/25 text-[10px] hover:text-white/50 tracking-wider transition-colors font-bold -mt-1">
                          EDIT
                        </button>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-white/30 text-xs">Name</span>
                          <span className="text-white text-xs font-medium">{form.name}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-white/30 text-xs">Email</span>
                          <span className="text-white text-xs font-medium">{form.email}</span>
                        </div>
                        {form.phone && (
                          <div className="flex justify-between items-center">
                            <span className="text-white/30 text-xs">Phone</span>
                            <span className="text-white text-xs font-medium">{form.phone}</span>
                          </div>
                        )}
                        {requiresShipping && (
                          <div className="pt-3 mt-1 border-t border-white/5 flex items-start gap-2">
                            {isPickup ? <Store className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> : <MapPin className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />}
                            <div>
                              <p className="text-white/25 text-[9px] font-bold tracking-widest mb-1">{isPickup ? 'COLLECTION' : 'DELIVERY'}</p>
                              {isPickup ? (
                                <p className="text-white/50 text-xs leading-relaxed">
                                  Collect from Bromborough — ready same day if before 1pm Mon–Thu. Billing: {form.address_line1}{form.address_line2 ? `, ${form.address_line2}` : ''}, {form.city}, {form.postcode}
                                </p>
                              ) : (
                                <p className="text-white/50 text-xs leading-relaxed">
                                  {form.address_line1}{form.address_line2 ? `, ${form.address_line2}` : ''}, {form.city}, {form.postcode}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Gift card / Discount code — merged */}
                    <div className="bg-white/[0.02] border border-white/5 p-4">
                      <div className="flex gap-2 mb-3">
                        <button
                          onClick={() => setCodeTab('gift')}
                          className={`flex items-center gap-2 px-3 py-1.5 border transition-all ${codeTab === 'gift' ? 'border-primary/40 bg-primary/10 text-primary' : 'border-white/5 text-white/40 hover:text-white/70'}`}
                        >
                          <Gift className="w-3 h-3" />
                          <span className="text-[10px] font-bold tracking-[0.15em]">GIFT CARD</span>
                        </button>
                        <button
                          onClick={() => setCodeTab('discount')}
                          className={`flex items-center gap-2 px-3 py-1.5 border transition-all ${codeTab === 'discount' ? 'border-primary/40 bg-primary/10 text-primary' : 'border-white/5 text-white/40 hover:text-white/70'}`}
                        >
                          <Ticket className="w-3 h-3" />
                          <span className="text-[10px] font-bold tracking-[0.15em]">DISCOUNT CODE</span>
                        </button>
                      </div>

                      {codeTab === 'gift' ? (
                        !appliedGiftCard ? (
                          <div className="flex gap-2">
                            <Input
                              value={giftCardCode}
                              onChange={e => setGiftCardCode(e.target.value)}
                              className={inputCls + ' uppercase h-9'}
                              placeholder="ENTER GIFT CARD CODE"
                            />
                            <button
                              onClick={handleApplyGiftCard}
                              disabled={checkingCode}
                              className="bg-white/5 border border-white/10 hover:border-primary/40 text-white/70 hover:text-white font-bold text-[10px] tracking-widest px-4 transition-all disabled:opacity-40 flex items-center gap-2 h-9"
                            >
                              {checkingCode ? <Loader2 className="w-3 h-3 animate-spin" /> : 'APPLY'}
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between bg-primary/5 border border-primary/20 px-3 py-2">
                            <div>
                              <p className="text-primary font-mono font-bold text-xs">{appliedGiftCard.code}</p>
                              <p className="text-white/50 text-[10px] mt-0.5">
                                Balance £{appliedGiftCard.balance.toFixed(2)} · Applied £{giftCardAppliedAmount.toFixed(2)}
                              </p>
                            </div>
                            <button onClick={handleRemoveGiftCard} className="text-white/30 hover:text-white/70 transition-colors">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )
                      ) : (
                        !appliedDiscount ? (
                          <div className="flex gap-2">
                            <Input
                              value={discountCodeInput}
                              onChange={e => setDiscountCodeInput(e.target.value)}
                              className={inputCls + ' uppercase h-9'}
                              placeholder="ENTER DISCOUNT CODE"
                            />
                            <button
                              onClick={handleApplyDiscount}
                              disabled={checkingDiscount}
                              className="bg-white/5 border border-white/10 hover:border-primary/40 text-white/70 hover:text-white font-bold text-[10px] tracking-widest px-4 transition-all disabled:opacity-40 flex items-center gap-2 h-9"
                            >
                              {checkingDiscount ? <Loader2 className="w-3 h-3 animate-spin" /> : 'APPLY'}
                            </button>
                          </div>
                        ) : (
                          <div className="bg-primary/5 border border-primary/20 px-3 py-2">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-primary font-mono font-bold text-xs">{appliedDiscount.code}</p>
                                <p className="text-white/50 text-[10px] mt-0.5">
                                  {appliedDiscount.type === 'percentage' ? `${appliedDiscount.value}% off` : `£${Number(appliedDiscount.value).toFixed(2)} off`} · Saved £{appliedDiscount.discountAmount.toFixed(2)}
                                </p>
                              </div>
                              <button onClick={handleRemoveDiscount} className="text-white/30 hover:text-white/70 transition-colors">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            {appliedDiscount.excludedItems?.length > 0 && (
                              <p className="text-amber-400/80 text-[10px] mt-1.5 leading-relaxed">
                                Excluded from discount: {appliedDiscount.excludedItems.join(', ')}
                              </p>
                            )}
                          </div>
                        )
                      )}
                      {codeTab === 'gift' && codeError && <p className="text-red-400 text-[10px] mt-2">{codeError}</p>}
                      {codeTab === 'discount' && discountError && <p className="text-red-400 text-[10px] mt-2">{discountError}</p>}
                    </div>

                    {/* Apple Pay — native button (verified domains + Safari only) */}
                    {!fullyCoveredByGiftCard && !hasGiftCardItem && (
                      <div style={{ display: applePayAvailable ? 'block' : 'none' }} className="bg-white/[0.02] border border-white/5 p-6">
                        <div id="sq-applepay" className="min-h-[48px] [&_button]:w-full" />
                      </div>
                    )}
                    {!fullyCoveredByGiftCard && !hasGiftCardItem && applePayAvailable && (
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-white/5" />
                        <span className="text-white/20 text-[10px] font-bold tracking-widest">OR PAY BY CARD</span>
                        <div className="flex-1 h-px bg-white/5" />
                      </div>
                    )}

                    {/* Card input (only if there's a remainder to charge) */}
                    {!fullyCoveredByGiftCard && (
                      <div className="bg-primary/[0.03] border border-primary/30 p-7">
                        <div className="flex items-center gap-3 mb-5">
                          <div className="w-8 h-8 bg-primary/15 border border-primary/30 flex items-center justify-center">
                            <CreditCard className="w-4 h-4 text-primary" />
                          </div>
                          <p className="text-white text-xs font-bold tracking-[0.15em]">CARD DETAILS</p>
                          <span className="text-primary text-[9px] font-bold tracking-widest bg-primary/10 border border-primary/20 px-2 py-0.5">RECOMMENDED</span>
                          <div className="flex-1 h-px bg-primary/20" />
                        </div>
                        <div id="sq-card" className="min-h-[48px]" />
                        <div className="flex items-start gap-2 mt-4">
                          <Shield className="w-3.5 h-3.5 text-primary/70 mt-0.5 shrink-0" />
                          <p className="text-white/40 text-[10px] leading-relaxed">
                            Payments secured and processed by Square. We never store card data. Please use a card registered to your address to avoid payment failures.
                          </p>
                        </div>
                      </div>
                    )}

                    {!fullyCoveredByGiftCard && !hasGiftCardItem && !afterpayUnavailable && (
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-white/5" />
                        <span className="text-white/20 text-[10px] font-bold tracking-widest">OR PAY BY CLEARPAY</span>
                        <div className="flex-1 h-px bg-white/5" />
                      </div>
                    )}

                    {/* Clearpay / Afterpay — pay in 4 interest-free instalments */}
                    {!fullyCoveredByGiftCard && !hasGiftCardItem && (
                      <div className="bg-white/[0.02] border border-white/5 p-6">
                        <SectionHeader icon={Wallet} label="CLEARPAY — PAY IN 4" />
                        {afterpayUnavailable ? (
                          <p className="text-white/30 text-xs leading-relaxed">{afterpayUnavailable}</p>
                        ) : (
                          <>
                            <div id="sq-afterpay" className="min-h-[48px]" />
                            <p className="text-white/30 text-[10px] mt-3 leading-relaxed">
                              Pay in 4 interest-free instalments with Clearpay. You'll complete payment on Clearpay's secure page.
                            </p>
                          </>
                        )}
                      </div>
                    )}

                    {fullyCoveredByGiftCard && (
                      <div className="bg-primary/5 border border-primary/20 p-4 flex items-center gap-3">
                        <Gift className="w-4 h-4 text-primary shrink-0" />
                        <p className="text-white/70 text-xs">Your gift card fully covers this order. No card payment required.</p>
                      </div>
                    )}

                    {/* Error */}
                    <AnimatePresence>
                      {error && (
                        <motion.p initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                          className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-4 py-3">
                          {error}
                        </motion.p>
                      )}
                    </AnimatePresence>

                    <motion.button
                      onClick={handlePayment}
                      disabled={loading}
                      whileHover={!loading ? { scale: 1.01 } : {}}
                      whileTap={!loading ? { scale: 0.99 } : {}}
                      className="w-full bg-primary hover:bg-primary/90 text-white font-black text-xs tracking-widest py-4 transition-all flex items-center justify-center gap-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          PROCESSING PAYMENT...
                        </>
                      ) : fullyCoveredByGiftCard ? (
                        <>
                          <Gift className="w-3.5 h-3.5" />
                          PAY WITH GIFT CARD
                        </>
                      ) : (
                        <>
                          <Lock className="w-3.5 h-3.5" />
                          PAY £{chargeAmount.toFixed(2)} SECURELY
                        </>
                      )}
                    </motion.button>

                    <button
                      type="button"
                      onClick={() => setStep('details')}
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 text-white/30 hover:text-white/70 font-bold text-xs tracking-widest py-3 transition-colors disabled:opacity-40"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" /> BACK TO DETAILS
                    </button>
                  </motion.div>
                )}

              </AnimatePresence>
            </div>

            {/* Right: Order Summary */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="bg-white/[0.02] border border-white/5 p-6 sticky top-[7.5rem]"
              >
                <p className="text-white/40 text-[10px] font-bold tracking-[0.18em] mb-5">ORDER SUMMARY</p>

                <div className="space-y-4 mb-6">
                  {items.map(item => (
                    <div key={item.cartItemId || item.product_id} className="flex gap-3 items-center">
                      <div className="w-12 h-12 bg-white/5 border border-white/5 flex items-center justify-center shrink-0 overflow-hidden">
                        {item.type === 'booking'
                          ? <Calendar className="w-5 h-5 text-primary/60" />
                          : item.type === 'giftcard'
                            ? <Gift className="w-5 h-5 text-primary/60" />
                            : item.product_image_url
                              ? <img src={item.product_image_url} alt={item.product_name} className="w-full h-full object-cover" />
                              : <ImageIcon className="w-4 h-4 text-white/15" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-xs font-semibold truncate leading-tight">
                          {item.type === 'booking' ? (item.product_name || item.experience_type) : item.product_name}
                        </p>
                        <p className="text-white/30 text-[11px] mt-0.5">
                          {item.type === 'booking'
                            ? `${item.date} @ ${item.time_slot}`
                            : item.type === 'giftcard'
                              ? (item.delivery_method === 'post' ? 'Postal gift card' : 'Email gift card')
                              : `Qty: ${item.quantity}`}
                        </p>
                      </div>
                      <p className="text-white text-sm font-bold shrink-0">£{(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                <div className="border-t border-white/5 pt-4 space-y-2.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-white/30">Subtotal</span>
                    <span className="text-white/70">£{totalPrice.toFixed(2)}</span>
                  </div>
                  {appliedDiscount && (
                    <div className="flex justify-between text-xs">
                      <span className="text-primary/80">Discount ({appliedDiscount.code})</span>
                      <span className="text-primary">-£{appliedDiscount.discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {appliedGiftCard && (
                    <div className="flex justify-between text-xs">
                      <span className="text-primary/80">Gift card ({appliedGiftCard.code})</span>
                      <span className="text-primary">-£{appliedGiftCard.appliedAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {requiresShipping && (
                    isPickup ? (
                      <div className="flex justify-between text-xs">
                        <span className="text-white/30 flex items-center gap-1.5">
                          <Store className="w-3 h-3" /> Collection
                        </span>
                        <span className="text-primary font-bold">FREE</span>
                      </div>
                    ) : (
                      <div className="flex justify-between text-xs">
                        <span className="text-white/30 flex items-center gap-1.5">
                          <Truck className="w-3 h-3" /> Shipping
                          {shippingRate && <span className="text-white/20">· {shippingRate.name}</span>}
                        </span>
                        <span className="text-white/70">
                          {shippingLoading ? '…' : shippingAmount > 0 ? `£${shippingAmount.toFixed(2)}` : 'Calculated at checkout'}
                        </span>
                      </div>
                    )
                  )}
                  {delayedShippingAmount > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-white/30 flex items-center gap-1.5">
                        <Truck className="w-3 h-3" /> Second despatch
                      </span>
                      <span className="text-white/70">£{delayedShippingAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-3 border-t border-white/5">
                    <span className="text-white/60 font-bold text-xs tracking-widest">TOTAL</span>
                    <span className="text-primary font-black text-2xl tracking-tight">
                      {chargeAmount > 0 ? `£${chargeAmount.toFixed(2)}` : '£0.00'}
                    </span>
                  </div>
                  {appliedGiftCard && (
                    <p className="text-white/30 text-[10px] pt-1">Gift card balance applied at checkout.</p>
                  )}
                </div>

                <div className="mt-5 pt-5 border-t border-white/5 flex items-start gap-2">
                  <Shield className="w-3.5 h-3.5 text-white/15 mt-0.5 shrink-0" />
                  <p className="text-white/20 text-[10px] leading-relaxed">
                    {isPickup
                      ? 'Collect from Bromborough — ready same day if ordered before 1pm Mon–Thu. We email you when it’s ready to collect.'
                        : requiresShipping
                        ? 'Next day UK mainland delivery on orders before 1pm Mon–Thu. Call 0151 528 3001 for enquiries.'
                        : 'No physical delivery required for this order.'}
                  </p>
                </div>
              </motion.div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}